import React from 'react';
import {
  MergeType as MergeIcon,
  PersonAdd as PersonAddIcon,
  Star as StarIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  LinearProgress,
  Radio,
  Typography,
} from '@mui/material';
import type { MatchedPatientData } from '@store/generated/admin';

import type { PotentialMatch } from '../types/patient-matching';
import { getConfidenceLevel } from '../types/patient-matching';

// Helper to format patient data for display
const formatPatientInfo = (data: any) => {
  if (!data) return {};

  return {
    name: `${data.first_name || ''} ${data.last_name || ''}`.trim() || 'Unknown',
    dateOfBirth: data.date_of_birth || data.dob || 'Unknown',
    phone: data.phone || data.phone_number || 'Unknown',
    email: data.email || 'Unknown',
    address: data.address ?
      `${data.address.street || ''} ${data.address.city || ''} ${data.address.state || ''} ${data.address.zip || ''}`.trim() :
      'Unknown',
    mrn: data.mrn || data.patient_id || data.id || 'Unknown',
    initials: `${(data.first_name || '')[0] || ''}${(data.last_name || '')[0] || ''}`.toUpperCase() || '??'
  };
};

interface PatientInfoDisplayProps {
  patient: ReturnType<typeof formatPatientInfo>;
  showAvatar?: boolean;
}

const PatientInfoDisplay: React.FC<PatientInfoDisplayProps> = ({ patient, showAvatar = true }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
    {showAvatar && (
      <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48, fontSize: '1.2rem' }}>
        {patient.initials}
      </Avatar>
    )}
    <Box sx={{ flex: 1 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
        {patient.name}
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Date of Birth:</strong> {patient.dateOfBirth}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Medical Record #:</strong> {patient.mrn}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Phone:</strong> {patient.phone}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Email:</strong> {patient.email}
          </Typography>
          {patient.address !== 'Unknown' && (
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Address:</strong> {patient.address}
            </Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  </Box>
);

interface ExtractedPatientSectionProps {
  patient: MatchedPatientData;
}

export const ExtractedPatientSection: React.FC<ExtractedPatientSectionProps> = ({ patient }) => {
  const patientInfo = formatPatientInfo(patient.imported_patient_data);

  return (
    <Card sx={{ bgcolor: 'primary.50', mb: 3, border: '2px solid', borderColor: 'primary.main' }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <Typography variant="h6" color="primary">
            📥 Extracted Patient (From Import File)
          </Typography>
          <Chip
            label="NEW"
            color="primary"
            size="small"
            variant="filled"
          />
        </Box>
        <PatientInfoDisplay patient={patientInfo} />
        <Box sx={{ mt: 2, p: 1, bgcolor: 'primary.100', borderRadius: 1 }}>
          <Typography variant="body2" color="primary.dark">
            ℹ️ This patient data was extracted from the import file and needs to be processed.
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

interface TargetPatientCardProps {
  match: PotentialMatch;
  isBest: boolean;
  isSelected: boolean;
  onSelect: () => void;
}

const TargetPatientCard: React.FC<TargetPatientCardProps> = ({
  match,
  isBest,
  isSelected,
  onSelect
}) => {
  const patientInfo = formatPatientInfo(match.patient_data);
  const confidenceInfo = getConfidenceLevel(match.confidence);

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        cursor: 'pointer',
        border: isSelected ? '3px solid' : '2px solid',
        borderColor: isSelected ? 'primary.main' : 'divider',
        bgcolor: isSelected ? 'primary.50' : 'background.paper',
        '&:hover': { borderColor: 'primary.light', transform: 'translateY(-2px)' },
        transition: 'all 0.2s ease-in-out'
      }}
      onClick={onSelect}
    >
      <CardContent>
        <FormControlLabel
          value={match.patient_id}
          control={<Radio checked={isSelected} />}
          label={
            <Box sx={{ width: '100%', ml: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  {patientInfo.name}
                </Typography>
                <Chip
                  label="EXISTING"
                  color="secondary"
                  size="small"
                  variant="filled"
                />
                {isBest && (
                  <Chip
                    icon={<StarIcon />}
                    label="Best Match"
                    size="small"
                    color="primary"
                  />
                )}
                <Chip
                  label={`${match.confidence}% confidence`}
                  size="small"
                  color={confidenceInfo.color}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Match Confidence: {match.confidence}% - {confidenceInfo.description}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={match.confidence}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    '& .MuiLinearProgress-bar': {
                      backgroundColor:
                        confidenceInfo.color === 'error' ? 'error.main' :
                        confidenceInfo.color === 'warning' ? 'warning.main' :
                        confidenceInfo.color === 'info' ? 'info.main' : 'success.main'
                    }
                  }}
                />
              </Box>

              <PatientInfoDisplay patient={patientInfo} showAvatar={false} />

              {isSelected && (
                <Box sx={{ mt: 2, p: 1, bgcolor: 'primary.100', borderRadius: 1 }}>
                  <Typography variant="body2" color="primary.dark">
                    ✓ This patient will be kept and updated with merged information
                  </Typography>
                </Box>
              )}
            </Box>
          }
        />
      </CardContent>
    </Card>
  );
};

interface TargetSelectionSectionProps {
  matches: PotentialMatch[];
  selectedTarget: string | null;
  onTargetChange: (targetId: string) => void;
}

export const TargetSelectionSection: React.FC<TargetSelectionSectionProps> = ({
  matches,
  selectedTarget,
  onTargetChange
}) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="h6" gutterBottom>
      🎯 Step 1: Select Target Patient (will be kept and updated)
    </Typography>
    <Alert severity="info" sx={{ mb: 2 }}>
      This patient record will be kept and updated with information from merged patients.
    </Alert>

    {matches.map((match, index) => (
      <TargetPatientCard
        key={match.patient_id}
        match={match}
        isBest={index === 0}
        isSelected={selectedTarget === match.patient_id}
        onSelect={() => onTargetChange(match.patient_id)}
      />
    ))}
  </Box>
);

interface ExtractedPatientCheckboxProps {
  patient: MatchedPatientData;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

const ExtractedPatientCheckbox: React.FC<ExtractedPatientCheckboxProps> = ({
  patient,
  checked,
  onChange
}) => {
  const patientInfo = formatPatientInfo(patient.imported_patient_data);

  return (
    <Card variant="outlined" sx={{ mb: 2, bgcolor: checked ? 'success.50' : 'background.default' }}>
      <CardContent>
        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              onChange={(e) => onChange(e.target.checked)}
            />
          }
          label={
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                  {patientInfo.name} (Extracted Patient)
                </Typography>
                <Chip
                  label="From Import File"
                  size="small"
                  color="success"
                  variant="outlined"
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                This will add any new information from the import file to the target patient
              </Typography>
            </Box>
          }
        />
      </CardContent>
    </Card>
  );
};

interface ExistingPatientCheckboxProps {
  match: PotentialMatch;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

const ExistingPatientCheckbox: React.FC<ExistingPatientCheckboxProps> = ({
  match,
  checked,
  onChange
}) => {
  const patientInfo = formatPatientInfo(match.patient_data);
  const confidenceInfo = getConfidenceLevel(match.confidence);

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        bgcolor: checked ? 'error.50' : 'background.default',
        border: checked ? '1px solid' : '1px solid',
        borderColor: checked ? 'error.main' : 'divider'
      }}
    >
      <CardContent>
        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              onChange={(e) => onChange(e.target.checked)}
            />
          }
          label={
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                  {patientInfo.name} (Existing Patient)
                </Typography>
                <Chip
                  label={`${match.confidence}% confidence`}
                  size="small"
                  color={confidenceInfo.color}
                />
                {checked && (
                  <Chip
                    icon={<WarningIcon />}
                    label="Will be deleted"
                    size="small"
                    color="error"
                  />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary">
                MRN: {patientInfo.mrn} | DOB: {patientInfo.dateOfBirth}
              </Typography>
              {checked && (
                <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                  ⚠️ This patient record will be deleted after merging
                </Typography>
              )}
            </Box>
          }
        />
      </CardContent>
    </Card>
  );
};

interface SourceSelectionSectionProps {
  extractedPatient: MatchedPatientData;
  potentialMatches: PotentialMatch[];
  targetPatientId: string | null;
  mergeExtracted: boolean;
  mergeExistingIds: Set<string>;
  onMergeExtractedChange: (checked: boolean) => void;
  onMergeExistingChange: (newSet: Set<string>) => void;
}

export const SourceSelectionSection: React.FC<SourceSelectionSectionProps> = ({
  extractedPatient,
  potentialMatches,
  targetPatientId,
  mergeExtracted,
  mergeExistingIds,
  onMergeExtractedChange,
  onMergeExistingChange
}) => {
  const otherMatches = potentialMatches.filter(m => m.patient_id !== targetPatientId);

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        📋 Step 2: Select Patients to Merge into Target
      </Typography>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Selected patients will be merged into the target and then deleted. Their data will be consolidated.
      </Alert>

      {/* Extracted Patient Checkbox */}
      <ExtractedPatientCheckbox
        patient={extractedPatient}
        checked={mergeExtracted}
        onChange={onMergeExtractedChange}
      />

      {/* Other Existing Patients */}
      {otherMatches.length > 0 && (
        <>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Other Existing Patients:
          </Typography>
          {otherMatches.map(match => (
            <ExistingPatientCheckbox
              key={match.patient_id}
              match={match}
              checked={mergeExistingIds.has(match.patient_id)}
              onChange={(checked) => {
                const newSet = new Set(mergeExistingIds);
                if (checked) {
                  newSet.add(match.patient_id);
                } else {
                  newSet.delete(match.patient_id);
                }
                onMergeExistingChange(newSet);
              }}
            />
          ))}
        </>
      )}

      {otherMatches.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No other existing patients available to merge.
        </Typography>
      )}
    </Box>
  );
};

interface ActionButtonsSectionProps {
  canMerge: boolean;
  mergeCount: number;
  onMerge: () => void;
  onImportNew: () => void;
  isLoading?: boolean;
  targetPatientName?: string;
  sourceNames?: string[];
}

interface MergeSummarySectionProps {
  targetPatientName: string;
  sourceNames: string[];
  mergeCount: number;
  canMerge: boolean;
}

const MergeSummarySection: React.FC<MergeSummarySectionProps> = ({
  targetPatientName,
  sourceNames,
  mergeCount,
  canMerge
}) => {
  if (!canMerge || mergeCount === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
      <Typography variant="h6" gutterBottom>
        📋 Merge Preview
      </Typography>
      <Box sx={{ pl: 2 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>✅ Target (will be kept):</strong> {targetPatientName}
        </Typography>
        {sourceNames.length > 0 && (
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>❌ Sources (will be merged and deleted):</strong>
          </Typography>
        )}
        {sourceNames.map((name, index) => (
          <Typography key={index} variant="body2" sx={{ ml: 2, mb: 0.5 }} color="text.secondary">
            • {name}
          </Typography>
        ))}
        <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold', color: 'primary.main' }}>
          🎯 Result: 1 consolidated patient record
        </Typography>
      </Box>
    </Box>
  );
};

export const ActionButtonsSection: React.FC<ActionButtonsSectionProps> = ({
  canMerge,
  mergeCount,
  onMerge,
  onImportNew,
  isLoading = false,
  targetPatientName,
  sourceNames
}) => (
  <Box>
    {/* Merge Summary */}
    <MergeSummarySection
      targetPatientName={targetPatientName || ''}
      sourceNames={sourceNames || []}
      mergeCount={mergeCount}
      canMerge={canMerge}
    />

    {/* Action Buttons */}
    <Box sx={{ display: 'flex', gap: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
      <Button
        variant="contained"
        color="primary"
        startIcon={<MergeIcon />}
        onClick={onMerge}
        disabled={!canMerge || isLoading}
        size="large"
        sx={{ minWidth: 200 }}
      >
        {isLoading ? 'Merging...' : `Merge ${mergeCount} Patient${mergeCount !== 1 ? 's' : ''} into Target`}
      </Button>

      <Button
        variant="outlined"
        color="success"
        startIcon={<PersonAddIcon />}
        onClick={onImportNew}
        disabled={isLoading}
        size="large"
        sx={{ minWidth: 180 }}
      >
        Import as New Patient
      </Button>
    </Box>
  </Box>
);